import lintModule from "bpmn-js-bpmnlint";
import "bpmn-js-bpmnlint/dist/assets/css/bpmn-js-bpmnlint.css";
import { ModdleElement } from "bpmn-js/lib/BaseModeler";
import Modeler from "bpmn-js/lib/Modeler";
import NavigatedViewer from "bpmn-js/lib/NavigatedViewer";
import minimapModule from "diagram-js-minimap";
import EventBus, { EventBusEventCallback } from "diagram-js/lib/core/EventBus";
import * as bpmnlintPluginKuika from "../components/design-tools/flows/bpmnlint/bpmnlint-plugin-kuika";
import customModdleExtension from "../components/design-tools/flows/moddle/custom.json";
import customWFModelerOnlyElementsModule from "../components/design-tools/flows/moddle/customWFModelerOnlyElementsModule";
import customWFSharedElementsModule from "../components/design-tools/flows/moddle/customWFSharedElementsModule";
import { ScreenDesignContextProvider } from "../contexts/screen-design-context/screen-design-context-provider";
import { isInsideModalOverlay } from "../shared/utility/modal-keyboard-scope";

interface ISdInstanceWindow {
  getSdInstance?: () => { workflowCollabProvider?: { isWorkflowSessionActive?: () => boolean } };
}

/** Live workflow collab session on this tab? (designer-live's getSdInstance pattern). */
function isWorkflowCollabSessionActive(): boolean {
  const w: ISdInstanceWindow = window as unknown as ISdInstanceWindow;
  try {
    return w.getSdInstance?.()?.workflowCollabProvider?.isWorkflowSessionActive?.() === true;
  } catch {
    return false;
  }
}

/** Minimal shape of a moddle flow element needed by the shapeless-orphan sweep. */
export interface ISemanticFlowElement extends ModdleElement {
  id: string;
  flowElements?: ISemanticFlowElement[];
  sourceRef?: ISemanticFlowElement;
  targetRef?: ISemanticFlowElement;
  /** moddle type-hierarchy test (`bo.$instanceOf("bpmn:FlowNode")`); absent on plain test fixtures. */
  $instanceOf?: (type: string) => boolean;
}

/** Minimal shape of the bpmn definitions root needed by the shapeless-orphan sweep. */
interface ISemanticDefinitions extends ModdleElement {
  rootElements?: ISemanticFlowElement[];
}

/** Minimal shape of a rendered diagram element (only its id is needed by the sweep). */
interface IRenderedElement {
  id: string;
}

/** Minimal shape of the diagram-js element registry needed by the sweep. */
interface IElementRegistryLike {
  getAll(): IRenderedElement[];
}

/** Minimal shape of the diagram-js canvas needed to fit the diagram into the viewport. */
interface ICanvasZoom {
  zoom(mode: string, center: string): void;
}

/** Result of pruning shapeless flow nodes from a process's flow elements. */
export interface IShapelessPruneResult {
  /** Flow elements to keep — same array reference when nothing was removed. */
  kept: ISemanticFlowElement[];
  /** Ids of the elements that were removed (empty when nothing was pruned). */
  removedIds: string[];
}

/**
 * Pure filter: given a process's `flowElements`, the set of ids that actually have a rendered
 * shape, and a `isFlowNode` predicate, drop every FLOW NODE (event / task / gateway / subprocess)
 * with no rendered shape — an orphan the modeler kept in the semantic model but never drew — plus
 * every `bpmn:SequenceFlow` that is itself shapeless or references such an orphan (a dangling
 * edge). `isFlowNode` is injected (in production `bo.$instanceOf("bpmn:FlowNode")`) so this stays
 * unit-testable and, crucially, so NON-flow-node flow elements — `bpmn:DataObject`,
 * `bpmn:DataObjectReference`, `bpmn:TextAnnotation` — which are legitimately shapeless in
 * `flowElements` are never touched. Returns the ORIGINAL array reference when there is nothing to
 * prune, so the caller can cheaply skip a no-op.
 *
 * NOTE: `renderedIds` must be non-empty — the caller guards an un-mounted / empty modeler, so an
 * empty registry is never passed here (it would otherwise classify every node as an orphan).
 * Exported for unit testing.
 */
export function computeShapelessNodePrune(
  flowElements: ISemanticFlowElement[],
  renderedIds: ReadonlySet<string>,
  isFlowNode: (fe: ISemanticFlowElement) => boolean
): IShapelessPruneResult {
  const orphanNodes: Set<ISemanticFlowElement> = new Set<ISemanticFlowElement>(
    flowElements.filter((fe: ISemanticFlowElement): boolean => isFlowNode(fe) && !renderedIds.has(fe.id))
  );
  const danglingFlows: Set<ISemanticFlowElement> = new Set<ISemanticFlowElement>(
    flowElements.filter((fe: ISemanticFlowElement): boolean => {
      if (fe.$type !== "bpmn:SequenceFlow") return false;
      const sourceOrphan: boolean = fe.sourceRef !== undefined && orphanNodes.has(fe.sourceRef);
      const targetOrphan: boolean = fe.targetRef !== undefined && orphanNodes.has(fe.targetRef);
      return !renderedIds.has(fe.id) || sourceOrphan || targetOrphan;
    })
  );

  if (orphanNodes.size === 0 && danglingFlows.size === 0) {
    return { kept: flowElements, removedIds: [] };
  }

  const kept: ISemanticFlowElement[] = flowElements.filter(
    (fe: ISemanticFlowElement): boolean => !orphanNodes.has(fe) && !danglingFlows.has(fe)
  );

  const removedIds: string[] = [];
  for (const orphan of orphanNodes) removedIds.push(orphan.id);
  for (const flow of danglingFlows) removedIds.push(flow.id);
  return { kept, removedIds };
}

interface IBpmnEvent {
  context?: {
    delta?: {
      x: number;
      y: number;
    };
    shape?: any;
    connection?: any;
    source?: any;
    target?: any;
    element?: any;
    newShape?: any;
    newLane?: any;
  };
  newSelection?: any;
  oldSelection?: any;
}

/** Narrow structural views of diagram-js's directEditing service — enough to gate activation. */
interface IDirectEditingBusinessObject {
  $type?: string;
}

interface IDirectEditingElement {
  type?: string;
  businessObject?: IDirectEditingBusinessObject;
  /** Set on external label elements; points at the shape the label belongs to. */
  labelTarget?: IDirectEditingElement;
}

interface IDirectEditingModule {
  activate(element: IDirectEditingElement): boolean;
}

/** A Happy Path phase group's business object — the moddle-extension attrs the interceptor sets. */
export interface IPhaseGroupBusinessObject {
  $type?: string;
  name?: string;
  isHappyPathPhase?: boolean;
  phaseId?: string;
}

/** Minimal view of a rendered phase-group element used to locate it by `phaseId` for a rename. */
export interface IPhaseGroupElement {
  businessObject?: IPhaseGroupBusinessObject;
  /** Set on external label elements — excluded so a phase's title label is not mistaken for it. */
  labelTarget?: unknown;
}

/**
 * Pure lookup: the phase GROUP whose moddle `phaseId` matches, excluding external labels (a label
 * carries the same businessObject as its group via `labelTarget`). Exported for unit testing.
 */
export function findPhaseGroupByPhaseId(
  elements: readonly IPhaseGroupElement[],
  phaseId: string
): IPhaseGroupElement | undefined {
  if (!phaseId) return undefined;
  return elements.find(
    (e: IPhaseGroupElement): boolean =>
      !e.labelTarget &&
      e.businessObject?.$type === "bpmn:Group" &&
      e.businessObject?.isHappyPathPhase === true &&
      e.businessObject?.phaseId === phaseId
  );
}

/** Narrow view of diagram-js `modeling` — just the label update used by phase rename. */
interface IModelingLabel {
  updateLabel(element: IPhaseGroupElement, label: string): void;
}

export class BpmnService {
  private modeler: Modeler | NavigatedViewer | undefined;

  private eventHandlers: Map<string, EventBusEventCallback<unknown>> = new Map();

  // Register event handlers before modeler is created
  onBpmnEvent(events: { [eventName: string]: (event: any) => void }): void {
    Object.entries(events).forEach(([eventName, handler]) => {
      this.eventHandlers.set(eventName, handler);
    });
  }

  async createModeler(container: HTMLElement | null, providerInstance: ScreenDesignContextProvider): Promise<Modeler> {
    const initialLintActive = readLintingActive();

    // Destroy any previous modeler before creating a new one. bpmn-js binds
    // keyboard handlers to document.body (a permanent GC root) via
    // `keyboard: { bindTo: document.body }`, so an orphaned modeler keeps its
    // entire diagram-js/moddle/minimap/lint graph alive. This method runs on
    // every workflow switch, so without the teardown each switch leaks a Modeler.
    this.destroyModeler();

    this.modeler = new Modeler({
      container: container || undefined,
      moddleExtensions: { custom: customModdleExtension },
      additionalModules: [
        customWFSharedElementsModule(),
        customWFModelerOnlyElementsModule(providerInstance),
        minimapModule,
        lintModule
      ],
      keyboard: { bindTo: document.body },
      linting: { active: initialLintActive }
    });
    this.patchOverlayZOrderForContextPadAndLint();

    attachLintingTogglePersistence(this.modeler);

    __installLintOverlayCssOnce();

    const m: any = this.modeler;
    const linting = m.get("linting");
    const eventBus = m.get("eventBus");
    const elementRegistry = m.get("elementRegistry");
    try {
      eventBus.on("canvas.viewbox.changed", () => {
        try {
          this.moveSchemaIssueIcons();
        } catch {}
      });
    } catch {}

    __setLintCtx(this.modeler, providerInstance);

    const attachWfSteps = () => {
      try {
        const steps = extractWfStepsFromProvider(providerInstance);
        __bakeStepsIntoDefinitions(this.modeler, steps);
      } catch {}
    };

    eventBus.on("import.done", () => {
      try {
        attachWfSteps();
        __setLintCtx(this.modeler, providerInstance);
        const { config, resolver } = buildBpmnlintOptions(this.modeler, providerInstance, false);
        linting.setLinterConfig({ config, resolver });
        linting.update();
        const all = elementRegistry.getAll ? elementRegistry.getAll() : [];
        eventBus.fire("elements.changed", { elements: all });
        startLintAutoAttach(this.modeler, providerInstance, 300, 60000);
        try {
          const canvas = m.get("canvas");
          const vb = canvas.viewbox();
          canvas.viewbox(vb);
          // Fit & centre the diagram on open. bpmn-js does not auto-fit after import, so a large
          // swimlane process would otherwise open scrolled off-screen at 100%. Fires on initial
          // load and on every workflow switch (each re-imports the stored XML).
          this.fitViewport();
        } catch {}
      } catch (e) {
        console.error("[lint] import.done handler error (modeler):", e);
      }
      this.patchOverlayZOrderForContextPadAndLint();
    });

    eventBus.on("selection.changed", () => {
      attachWfSteps();
      __setLintCtx(this.modeler, providerInstance);
      linting.update();
      // Flag multi-selection so the CSS can drop the per-node blue body fill (kept for a single
      // selection) — a marquee of many tinted nodes is heavy; outline + blue stroke still mark them.
      try {
        const selection = m.get("selection");
        const selected: unknown[] = selection && typeof selection.get === "function" ? selection.get() : [];
        const container = m.get("canvas").getContainer();
        if (container && container.classList) {
          container.classList.toggle("kuika-multi-select", selected.length > 1);
        }
      } catch {}
    });

    eventBus.on("commandStack.changed", () => {
      attachWfSteps();
      __setLintCtx(this.modeler, providerInstance);
      linting.update();
    });

    eventBus.on("linting.completed", (e: any) => {
      const issues = (e && e.issues) || {};
      const ids = Object.keys(issues);
      let errs = 0;
      let warns = 0;
      ids.forEach((id) =>
        (issues[id] || []).forEach((r: any) => {
          if (r.category === "error") errs++;
          else if (r.category === "warn") warns++;
        })
      );
      this.moveSchemaIssueIcons();
    });

    (window as any).bpmnModeler = this.modeler;

    installBpmnWindowFunctions();

    this.initializeEventHandling();
    this.patchKeyboardModule();
    this.patchDirectEditingModule();
    return this.modeler as Modeler;
  }

  /**
   * Pool and lane labels are CODE-managed: the pool carries the process name and lanes are
   * named by their role assignment (handleLaneAssignmentChange), so a hand-typed label is
   * silently re-derived on a later load (KOA-18293 B2's trap). Block the inline label editor
   * for them at its single entry point — directEditing.activate covers double-click, F2, and
   * the auto-activate after create. Programmatic renames (modeling.updateProperties, collab
   * ops, the DSL executor) don't go through directEditing and stay unaffected; so do phase
   * groups (bpmn:Group) and every other element's label.
   */
  private patchDirectEditingModule(): void {
    if (!this.modeler) return;
    try {
      const directEditing: IDirectEditingModule | undefined = this.modeler.get("directEditing") as
        | IDirectEditingModule
        | undefined;
      if (!directEditing?.activate) return;
      const originalActivate: (element: IDirectEditingElement) => boolean = directEditing.activate.bind(directEditing);
      directEditing.activate = (element: IDirectEditingElement): boolean => {
        // A click can land on the label element itself; its labelTarget is the real shape.
        const resolved: IDirectEditingElement = element?.labelTarget ?? element;
        const type: string | undefined = resolved?.type ?? resolved?.businessObject?.$type;
        if (type === "bpmn:Participant" || type === "bpmn:Lane") return false;
        return originalActivate(element);
      };
    } catch (error) {
      console.error("Failed to patch directEditing module:", error);
    }
  }

  private patchKeyboardModule(): void {
    if (!this.modeler) return;

    try {
      const keyboard = this.modeler.get("keyboard") as any;
      if (!keyboard || !keyboard._keyHandler) return;

      const originalKeyHandler = keyboard._keyHandler.bind(keyboard);

      keyboard._keyHandler = (event: KeyboardEvent) => {
        const isInModal: boolean = isInsideModalOverlay(event.target);
        const isInMonacoEditor = event.target instanceof Element && event.target.closest(".monaco-editor") !== null;
        const isContentEditable =
          event.target instanceof HTMLElement &&
          (event.target.contentEditable === "true" || event.target.isContentEditable === true);
        const tagName = event.target instanceof Element ? event.target.tagName?.toUpperCase() : "";
        const isInput = tagName === "INPUT" || tagName === "TEXTAREA";

        if (isInModal || isInMonacoEditor || isContentEditable || isInput) {
          return;
        }

        // Workflow collab: bpmn-js's native commandStack undo/redo would revert OTHER
        // users' remotely-applied commands. work-flow-window routes Ctrl+Z/Y to the
        // per-user op stacks; swallow them here so the default binding can't double-fire.
        const isUndoRedoKey =
          (event.ctrlKey || event.metaKey) &&
          (event.key === "z" || event.key === "Z" || event.key === "y" || event.key === "Y");
        if (isUndoRedoKey && isWorkflowCollabSessionActive()) {
          return;
        }

        return originalKeyHandler(event);
      };
    } catch (error) {
      console.error("Failed to patch keyboard module:", error);
    }
  }

  async createViewer(container: HTMLElement | null): Promise<NavigatedViewer> {
    const initialLintActive = readLintingActive();

    // See createModeler: tear down the previous instance before creating a new
    // one so its keyboard/document.body binding and object graph aren't leaked.
    this.destroyModeler();

    this.modeler = new NavigatedViewer({
      container: container || undefined,
      additionalModules: [customWFSharedElementsModule(), minimapModule, lintModule],
      linting: { active: initialLintActive }
    });

    attachLintingTogglePersistence(this.modeler);

    __installLintOverlayCssOnce();

    const m: any = this.modeler;
    const linting = m.get("linting");
    const eventBus = m.get("eventBus");
    const elementRegistry = m.get("elementRegistry");

    const attachWfSteps = () => {
      try {
        __bakeStepsIntoDefinitions(this.modeler, undefined);
      } catch {}
    };

    eventBus.on("import.done", () => {
      try {
        attachWfSteps();
        const { config, resolver } = buildBpmnlintOptions(this.modeler, undefined, false);
        linting.setLinterConfig({ config, resolver });
        linting.update();
        startLintAutoAttach(
          this.modeler,
          (globalThis as any).getSdInstance ? (globalThis as any).getSdInstance() : undefined,
          300,
          60000
        );
        const all = elementRegistry.getAll ? elementRegistry.getAll() : [];
        eventBus.fire("elements.changed", { elements: all });
      } catch (e) {
        console.error("[lint] import.done handler error (viewer):", e);
      }
    });

    eventBus.on("selection.changed", () => {
      attachWfSteps();
      linting.update();
    });

    eventBus.on("commandStack.changed", () => {
      attachWfSteps();
      linting.update();
    });

    eventBus.on("linting.completed", (e: any) => {
      const issues = (e && e.issues) || {};
      const ids = Object.keys(issues);
      let errs = 0;
      let warns = 0;
      ids.forEach((id) =>
        (issues[id] || []).forEach((r: any) => {
          if (r.category === "error") errs++;
          else if (r.category === "warn") warns++;
        })
      );
      this.moveSchemaIssueIcons();
    });

    (window as any).viewer = this.modeler;

    installBpmnWindowFunctions();

    return this.modeler as NavigatedViewer;
  }

  private moveSchemaIssueIcons = () => {
    const m: any = this.modeler;
    if (!m) return;

    const container: HTMLElement | null = (m._container as HTMLElement) || null;
    if (!container) return;

    const elementRegistry = m.get?.("elementRegistry", false);
    if (!elementRegistry) return;

    const all = elementRegistry.getAll ? elementRegistry.getAll() : [];

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    for (const el of all) {
      if (!el) continue;
      if (el.type === "label") continue;
      if ((el as any).waypoints) continue;

      if (
        typeof el.x !== "number" ||
        typeof el.y !== "number" ||
        typeof el.width !== "number" ||
        typeof el.height !== "number"
      ) {
        continue;
      }

      minX = Math.min(minX, el.x);
      minY = Math.min(minY, el.y);
      maxX = Math.max(maxX, el.x + el.width);
      maxY = Math.max(maxY, el.y + el.height);
    }

    if (!Number.isFinite(minX) || !Number.isFinite(minY) || !Number.isFinite(maxX) || !Number.isFinite(maxY)) {
      return;
    }

    const PAD_X = 8;
    const PAD_Y = 8;

    const targetX = minX + PAD_X;
    const targetY = minY + PAD_Y;

    const overlays = Array.from(container.querySelectorAll<HTMLElement>(".djs-overlay"));

    overlays.forEach((el, idx) => {
      const text = (el.textContent || "").trim();

      const isSchemaIssue = text.indexOf("Diagram must contain at least one") !== -1;

      if (!isSchemaIssue) {
        return;
      }

      el.style.position = "absolute";
      el.style.left = `${targetX}px`;
      el.style.top = `${targetY}px`;
      el.style.right = "auto";
      el.style.bottom = "auto";
      el.style.margin = "0";
    });
  };

  private patchOverlayZOrderForContextPadAndLint(): void {
    const m: any = this.modeler;
    if (!m) return;

    const contextPad: any = m.get?.("contextPad", false);
    const container: HTMLElement | null = (m._container as HTMLElement) || null;
    if (!contextPad || !container) return;

    const apply = () => {
      const padEl = container.querySelector(".djs-context-pad") as HTMLElement | null;
      const padOverlay = padEl?.closest(".djs-overlay") as HTMLElement | null;
      if (padOverlay) {
        padOverlay.style.zIndex = "5000";
      }

      const lintOverlays = container.querySelectorAll<HTMLElement>(".djs-overlay.djs-overlay-linting");
      lintOverlays.forEach((el) => {
        el.style.zIndex = "3000";
      });
    };

    const applyAfterPaint = () => {
      requestAnimationFrame(() => requestAnimationFrame(apply));
    };

    applyAfterPaint();

    if (!contextPad.__kuika_z_patched) {
      const origOpen = contextPad.open?.bind(contextPad);
      const origClose = contextPad.close?.bind(contextPad);

      if (typeof origOpen === "function") {
        contextPad.open = (...args: any[]) => {
          const r = origOpen(...args);
          applyAfterPaint();
          return r;
        };
      }

      if (typeof origClose === "function") {
        contextPad.close = (...args: any[]) => {
          const r = origClose(...args);
          applyAfterPaint();
          return r;
        };
      }

      contextPad.__kuika_z_patched = true;
    }
  }

  private destroyModeler(): void {
    if (this.modeler) {
      // bpmn-js-bpmnlint computes its overlays asynchronously (Linting.update() ->
      // lint().then(_formatIssues)) and has no destroy-time cancellation. Invalidate
      // its epoch guard so any in-flight lint's deferred callback bails
      // (self._lintStart !== lintStart) instead of calling getRootElement() on the
      // canvas we are about to destroy — which otherwise throws "Cannot read
      // properties of undefined (reading 'length')" once diagram-js deletes _planes
      // in Canvas._destroy(). KOA-18065.
      try {
        const m: any = this.modeler;
        const linting = m.get?.("linting", false);
        if (linting) linting._lintStart = null;
      } catch {}
      try {
        this.modeler.destroy();
      } catch {}
      this.modeler = undefined;
    }
    // createModeler/createViewer publish the instance on window (for test
    // automation + legacy helpers); clear those globals so a destroyed modeler
    // graph isn't pinned to window for the whole tab session.
    try {
      delete (window as any).bpmnModeler;
    } catch {}
    try {
      delete (window as any).viewer;
    } catch {}
  }

  resetModeler() {
    this.destroyModeler();
  }

  didElementChangeLane(element: ModdleElement, delta: { x: number; y: number }): boolean {
    const oldX = element.x - delta.x;
    const oldY = element.y - delta.y;
    const fakeOldShape = { ...element, x: oldX, y: oldY };

    const oldLane = this.findContainingLane(fakeOldShape);
    const newLane = this.findContainingLane(element);

    return oldLane?.id !== newLane?.id;
  }

  findParentSubprocess(element: ModdleElement): ModdleElement | undefined {
    let current = element;
    while (current.parent) {
      if (current.parent.$type === "bpmn:SubProcess") {
        return current.parent;
      }
      current = current.parent;
    }
    return undefined;
  }

  findContainingLane(shape: ModdleElement): ModdleElement | undefined {
    const elementRegistry = this.getElementRegistry();
    const lanes = elementRegistry.getAll().filter((el) => el.type === "bpmn:Lane");
    return lanes.find((lane) => {
      const lb = lane; // lane bounds
      return (
        shape.x >= lb.x &&
        shape.y >= lb.y &&
        shape.x + shape.width <= lb.x + lb.width &&
        shape.y + shape.height <= lb.y + lb.height
      );
    });
  }

  updateElementProperties(elementId: string, properties: any): void {
    const modeling = this.getModeling();
    const elementRegistry = this.getElementRegistry();

    const element = elementRegistry.get(elementId);

    if (element) {
      modeling.updateProperties(element, properties);
    }
  }

  /**
   * Rename the Happy Path phase GROUP whose moddle `phaseId` matches — via `modeling.updateLabel`,
   * so the HappyPathPhaseInterceptor's `element.updateLabel` hook re-centres the header label and
   * syncs `happyPathDefinition`, exactly as a canvas double-click rename would. Returns false when
   * no such group exists (e.g. a pool-less legacy phase with a model entry but no drawn column).
   */
  renamePhaseGroup(phaseId: string, label: string): boolean {
    if (!this.modeler) return false;
    const elementRegistry = this.getElementRegistry();
    const modeling: IModelingLabel | undefined = this.getModeling();
    if (!elementRegistry || !modeling) return false;
    const groups: IPhaseGroupElement[] = elementRegistry.getAll() as IPhaseGroupElement[];
    const group: IPhaseGroupElement | undefined = findPhaseGroupByPhaseId(groups, phaseId);
    if (!group) return false;
    if ((group.businessObject?.name ?? "").trim() === label.trim()) return true;
    modeling.updateLabel(group, label);
    return true;
  }

  SetUniqueLabel = (context) => {
    let label = context?.newLabel ? context.newLabel : context?.shape?.businessObject?.name;
    const id = context?.element?.id ? context.element.id : context.shape.id;
    if (label && label.length > 0) {
      const elementRegistry = this.getElementRegistry();
      const isUnique = this.isStepNameUnique(label, id, elementRegistry);
      if (!isUnique) {
        const typedName = label;
        let i = 1;
        while (!this.isStepNameUnique(label, id, elementRegistry)) {
          label = `${typedName} ${i}`;
          i++;
        }
        const modeling = this.getModeling();
        const element = context?.element ? context.element : context.shape;
        modeling.updateProperties(element, { name: label });
      }
    }
  };

  private isStepNameUnique = (label, id, elementRegistry) => {
    const elements = elementRegistry
      .getAll()
      .filter((el) => el.type === "bpmn:Task" || el.type === "bpmn:UserTask" || el.type === "bpmn:CallActivity");
    return !elements.some((element) => element.businessObject.name === label && element.id != id);
  };

  private getModeling(): any {
    if (!this.modeler) return undefined;
    return this.modeler.get("modeling");
  }

  private getElementRegistry(): any {
    if (!this.modeler) return undefined;
    return this.modeler.get("elementRegistry");
  }

  private initializeEventHandling(): void {
    if (!this.modeler) return;

    const eventBus = this.modeler.get("eventBus") as EventBus;

    // Register all stored handlers
    this.eventHandlers.forEach((handler, eventName) => {
      eventBus.on(eventName, handler);
    });
  }

  getModeler(): Modeler | NavigatedViewer | undefined {
    return this.modeler;
  }

  getElementById(id: string): ModdleElement | undefined {
    if (!this.modeler) return undefined;
    const elementRegistry = this.modeler.get("elementRegistry") as any;
    return elementRegistry.get(id);
  }

  getAllElements(filter?: { type?: string }): ModdleElement[] {
    if (!this.modeler) return [];
    const elementRegistry = this.modeler.get("elementRegistry") as any;
    let elements = elementRegistry.getAll();
    if (filter) {
      elements = elements.filter((e) => e.type === filter.type);
    }
    return elements;
  }

  findElementsInLane(laneId: string): any[] {
    if (!this.modeler) return [];

    const elementRegistry = this.modeler.get("elementRegistry") as any;
    if (!elementRegistry) return [];

    const laneElement = elementRegistry.get(laneId);
    if (!laneElement) return [];

    const all = elementRegistry.getAll();
    return all.filter((el: any) => {
      return (
        el.x >= laneElement.x &&
        el.y >= laneElement.y &&
        el.x + el.width <= laneElement.x + laneElement.width &&
        el.y + el.height <= laneElement.y + laneElement.height
      );
    });
  }

  /** Fit & centre the whole diagram into the viewport. Used on open and by the editor's Fit control. */
  fitViewport(): void {
    if (!this.modeler) return;
    try {
      const canvas = this.modeler.get("canvas") as ICanvasZoom;
      canvas.zoom("fit-viewport", "auto");
    } catch (err) {
      console.warn("[BpmnService] fitViewport failed:", err);
    }
  }

  async exportXml(): Promise<string> {
    if (!this.modeler) return "";
    try {
      // Safety-net sync: the JSON model is reconciled against the rendered diagram on every
      // save (WorkflowProvider.syncWorkflowNodes), but the bpmn SEMANTIC model is not — so an
      // orphan flow-node businessObject (event / task / gateway in process.flowElements with no
      // BPMNShape) would be serialized into the XML, stay invisible on canvas, and surface as a
      // phantom lint error (and a wrong generated definition). Prune those here, the single
      // chokepoint feeding both storage and code generation, so exported XML matches what is rendered.
      this.pruneShapelessFlowNodes();
      const result = await (this.modeler as Modeler).saveXML({ format: true });
      return result.xml || "";
    } catch (err) {
      console.error("Error exporting XML:", err);
      return "";
    }
  }

  /**
   * Remove flow-node businessObjects (events / tasks / gateways / subprocesses) that have no
   * rendered shape (not in the elementRegistry) from every process's `flowElements` — plus any
   * dangling sequence flows — recursing into embedded subprocesses. These orphans cannot be
   * removed via `modeling.*` (there is no shape to act on), so we prune the moddle tree directly —
   * the same collection-remove bpmn-js's own BpmnUpdater performs. Runs only when the diagram
   * actually has rendered elements, so an un-mounted / empty modeler can never be mistaken for
   * "everything is an orphan".
   */
  private pruneShapelessFlowNodes(): void {
    if (!this.modeler) return;
    try {
      const elementRegistry = this.modeler.get("elementRegistry") as IElementRegistryLike;
      const rendered: IRenderedElement[] = elementRegistry.getAll();
      if (rendered.length === 0) return;
      const renderedIds: Set<string> = new Set<string>(rendered.map((el: IRenderedElement): string => el.id));

      // A flow element that must be drawn iff it is a `bpmn:FlowNode` (every event, task, gateway,
      // subprocess). Non-flow-node flow elements (DataObject, DataObjectReference, TextAnnotation)
      // are legitimately shapeless and must be left alone.
      const isFlowNode = (fe: ISemanticFlowElement): boolean =>
        typeof fe.$instanceOf === "function" && fe.$instanceOf("bpmn:FlowNode");

      const definitions = (this.modeler as Modeler).getDefinitions() as ISemanticDefinitions | undefined;
      const rootElements: ISemanticFlowElement[] = definitions?.rootElements ?? [];
      const processes: ISemanticFlowElement[] = rootElements.filter(
        (e: ISemanticFlowElement): boolean => e.$type === "bpmn:Process"
      );

      for (const process of processes) {
        this.pruneContainerFlowElements(process, renderedIds, isFlowNode);
      }
    } catch (err) {
      // Never let the safety-net sweep block a save — a failed prune degrades to the prior
      // behaviour (orphan stays in XML), it does not lose the user's work.
      console.warn("[BpmnService] pruneShapelessFlowNodes failed:", err);
    }
  }

  /** Prune one flow-element container (process or subprocess), then recurse into surviving subprocesses. */
  private pruneContainerFlowElements(
    container: ISemanticFlowElement,
    renderedIds: ReadonlySet<string>,
    isFlowNode: (fe: ISemanticFlowElement) => boolean
  ): void {
    const flowElements: ISemanticFlowElement[] = container.flowElements ?? [];
    if (flowElements.length === 0) return;

    const result: IShapelessPruneResult = computeShapelessNodePrune(flowElements, renderedIds, isFlowNode);
    if (result.removedIds.length > 0) {
      container.flowElements = result.kept;
      for (const removedId of result.removedIds) {
        console.log(`[BpmnService] Pruned shapeless flow element ${removedId} from ${container.id}`);
      }
    }

    for (const child of container.flowElements ?? []) {
      if (Array.isArray(child.flowElements) && child.flowElements.length > 0) {
        this.pruneContainerFlowElements(child, renderedIds, isFlowNode);
      }
    }
  }

  relint = (): void => {
    const m: any = this.modeler;
    if (!m) return;

    try {
      const linting = m.get?.("linting", false);
      __setLintCtx(this.modeler);
      const canvas = m.get?.("canvas");
      const root = canvas?.getRootElement?.();
      if (!root) return;
      const defs = root?.businessObject?.$parent;
      const baked = defs && (defs as any).__kuika_wf_steps;
      __bakeStepsIntoDefinitions(this.modeler, baked);
      linting?.update?.();
    } catch {}

    try {
      const eventBus = m.get?.("eventBus", false);
      const elements = this.getAllElements();
      if (!elements?.length) return;
      eventBus?.fire?.("elements.changed", { elements });
    } catch {}
  };
}

function extractWfStepsFromProvider(providerInstance: any): any[] | undefined {
  const pick = (root: any): any[] | undefined =>
    root?.selectedWf?.nodes?.steps && Array.isArray(root.selectedWf.nodes.steps)
      ? root.selectedWf.nodes.steps
      : undefined;

  try {
    const p: any = providerInstance;

    let r =
      p?.workflowProvider?.state?.selectedWf?.nodes?.steps ||
      pick(p?.workflowProvider?.getProviderValues?.()) ||
      pick(p?.getScreenDesignContextProviderValues?.()) ||
      (globalThis as any)?.sdContext?.selectedWf?.nodes?.steps;

    if (Array.isArray(r)) return r;
  } catch {}

  return undefined;
}

let __kuikaLintCtx: {
  modeler?: any;
  providerInstance?: any;
  elementRegistry?: any;
  getWfSteps?: () => any[] | undefined;
} = {};

function __setLintCtx(modeler?: any, providerInstance?: any) {
  __kuikaLintCtx.modeler = modeler;
  // KOA-18199: __kuikaLintCtx is a MODULE SINGLETON shared by every modeler. relint()
  // (and the viewer path) call this without a providerInstance — overwriting with
  // undefined silently disabled every provider-backed rule (screen-assigned,
  // system-step-has-action, step-output-unbound: they bail on a null ctx) until the
  // next full setLinterConfig. An omitted arg now PRESERVES the existing instance.
  if (providerInstance !== undefined) {
    __kuikaLintCtx.providerInstance = providerInstance;
  }
  __kuikaLintCtx.elementRegistry = modeler?.get?.("elementRegistry");
  try {
    const existing = (globalThis as any).getSdInstance?.();
    if (!existing || typeof existing.getScreenDesignContextProviderValues !== "function") {
      (globalThis as any).getSdInstance = () => ({
        workflowProvider: {
          getSelectedWf: () => __kuikaLintCtx?.providerInstance?.workflowProvider?.state?.selectedWf ?? null
        }
      });
    }
  } catch {}
  __kuikaLintCtx.getWfSteps = () => {
    try {
      const canvas = __kuikaLintCtx.modeler?.get?.("canvas");
      const root = canvas?.getRootElement?.();
      const defs = root?.businessObject?.$parent;
      const baked = defs && (defs as any).__kuika_wf_steps;
      if (Array.isArray(baked)) return baked;
    } catch {}
    return extractWfStepsFromProvider(__kuikaLintCtx.providerInstance);
  };
  try {
    (globalThis as any).__kuika_LINT_CTX = __kuikaLintCtx;
  } catch {}
}

function __bakeStepsIntoDefinitions(modeler: any, steps?: any[]) {
  try {
    const arr = Array.isArray(steps) ? steps : [];
    __forceBakeSteps(modeler, arr);
    const defs = (modeler?.get?.("canvas")?.getRootElement?.()?.businessObject as any)?.$root;
    if (defs) (defs as any).__kuika_wf_steps = arr;
    (globalThis as any).__kuika_wf_steps = arr;
  } catch {}
}

function __deepLenSignature(x: any): string {
  try {
    if (!x || !Array.isArray(x)) return "none";
    const n = x.length;
    let keys = 0;
    for (let i = 0; i < Math.min(n, 10); i++) {
      const it = x[i];
      if (it && typeof it === "object") {
        if ("id" in it) keys++;
        if ("name" in it) keys++;
        if ("type" in it) keys++;
      }
    }
    return `len:${n}|k:${keys}`;
  } catch {
    return "err";
  }
}

function startLintAutoAttach(modeler: any, providerInstance: any, pollMs = 300, timeoutMs = 30000) {
  if (!modeler) return;
  // Idempotent per modeler: `import.done` fires again on every re-import (e.g.
  // external xml sync). Without this guard each call re-registers the eventBus
  // handlers below AND spawns another poll interval, stacking them on a
  // long-lived modeler. Attach once — the poll self-terminates after timeoutMs
  // and the on-demand commandStack/selection handlers keep steps baked after.
  if (modeler.__kuikaLintAutoAttached) return;
  modeler.__kuikaLintAutoAttached = true;

  __setLintCtx(modeler, providerInstance);
  const linting = modeler?.get?.("linting");
  const eventBus = modeler?.get?.("eventBus");
  const elementRegistry = modeler?.get?.("elementRegistry");
  const canvas = modeler?.get?.("canvas");

  let suspended = false;
  const hasRoot = () => {
    try {
      const root = canvas?.getRootElement?.();
      return !!root && !!root.businessObject;
    } catch {
      return false;
    }
  };

  eventBus?.on?.("import.start", () => {
    suspended = true;
  });
  eventBus?.on?.("import.done", () => {
    suspended = false;
  });

  eventBus?.on?.("diagram.destroy", () => {
    try {
      clearInterval(t);
    } catch {}
  });

  let lastSig = "none";
  let elapsed = 0;
  const t = setInterval(() => {
    if (suspended || !hasRoot()) {
      elapsed += pollMs;
      if (elapsed >= timeoutMs) {
        clearInterval(t);
      }
      return;
    }

    const steps = extractWfStepsFromProvider(providerInstance);
    const sig = __deepLenSignature(steps);

    if (sig !== lastSig) {
      lastSig = sig;
      __bakeStepsIntoDefinitions(modeler, steps);
      try {
        if (hasRoot()) {
          linting?.update?.();
          const all = elementRegistry?.getAll ? elementRegistry.getAll() : [];
          eventBus?.fire?.("elements.changed", { elements: all });
        }
      } catch (e) {}
    }

    elapsed += pollMs;
    if (elapsed >= timeoutMs) {
      clearInterval(t);
    }
  }, pollMs);

  eventBus?.on?.("commandStack.changed", () => {
    if (suspended || !hasRoot()) return;
    const steps = extractWfStepsFromProvider(providerInstance);
    __bakeStepsIntoDefinitions(modeler, steps);
  });

  eventBus?.on?.("selection.changed", () => {
    if (suspended || !hasRoot()) return;
    const steps = extractWfStepsFromProvider(providerInstance);
    __bakeStepsIntoDefinitions(modeler, steps);
  });
}

function buildBpmnlintOptions(modeler?: any, providerInstance?: any, verbose?: boolean) {
  const dbg = verbose ? console.log : () => {};
  const mod: any = bpmnlintPluginKuika;
  const plugin = mod?.default?.rules ? mod.default : mod?.rules ? mod : mod;

  __setLintCtx(modeler, providerInstance);

  const toFactory = (entry: any) => {
    if (typeof entry === "function") return entry;
    if (entry && typeof entry.default === "function") return entry.default;
    if (entry && typeof entry.check === "function") return () => entry;
    if (entry?.default && typeof entry.default.check === "function") return () => entry.default;
    return null;
  };

  const cacheKey = "__kuika_normalized_rules__";
  let normalizedRules: Record<string, Function> = (plugin as any)[cacheKey];
  if (!normalizedRules) {
    normalizedRules = {};
    const rawNames = Object.keys(plugin?.rules || {});
    rawNames.forEach((name) => {
      const f = toFactory(plugin.rules[name]);
      if (f) {
        normalizedRules[name] = () => f({ ctx: __kuikaLintCtx });
      }
    });
    (plugin as any)[cacheKey] = normalizedRules;
  }

  const config: any = { rules: {} };
  for (const name of Object.keys(normalizedRules)) {
    const severity = RULE_SEVERITY[name] ?? "error";
    if (severity === "off") continue;
    config.rules[`bpmnlint-plugin-kuika/${name}`] = severity;
    config.rules[`kuika/${name}`] = severity;
  }

  const resolver = {
    resolveRule(pkgName: string, ruleName?: string) {
      if ((pkgName === "bpmnlint-plugin-kuika" || pkgName === "kuika") && ruleName && normalizedRules[ruleName]) {
        return normalizedRules[ruleName];
      }
      return null;
    },
    resolveConfig() {
      return { rules: {} };
    }
  };

  return { config, resolver };
}

function __forceBakeSteps(modeler: any, steps: any[]) {
  const canvas = modeler?.get?.("canvas");
  const bpmnFactory = modeler?.get?.("bpmnFactory");
  const eventBus = modeler?.get?.("eventBus");
  if (!canvas || !bpmnFactory || !eventBus) return;

  const root = canvas.getRootElement();
  const bo: any = root?.businessObject;
  if (!bo) return;

  const defs: any = bo.$root;
  if (!defs || defs.$type !== "bpmn:Definitions") return;

  defs.$attrs = defs.$attrs || Object.create(null);

  if (!defs.extensionElements) {
    defs.extensionElements = bpmnFactory.create("bpmn:ExtensionElements", { values: [] });
  }

  try {
    defs.$attrs["wf:steps"] = JSON.stringify(steps || []);
  } catch {
    defs.$attrs["wf:steps"] = "[]";
  }
  eventBus.fire("elements.changed", { elements: [root] });
}

const RULE_SEVERITY: Record<string, "error" | "warn" | "off"> = {
  "gateway-min-outgoing": "warn",
  "label-required": "warn",
  "mi-instance-config": "warn",
  "loop-config": "warn"
};

const BPMN_LINT_ACTIVE_KEY = "kuika_bpmn_lint_active";

function readLintingActive(): boolean {
  try {
    if (typeof window === "undefined" || !window.localStorage) return true;
    const val = window.localStorage.getItem(BPMN_LINT_ACTIVE_KEY);
    if (val === "0" || val === "false") return false;
    if (val === "1" || val === "true") return true;
  } catch {}

  return true;
}

function storeLintingActive(active: boolean): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.setItem(BPMN_LINT_ACTIVE_KEY, active ? "1" : "0");
  } catch {}
}

function attachLintingTogglePersistence(modeler: any) {
  try {
    if (!modeler || typeof modeler.on !== "function") return;

    modeler.on("linting.toggle", (event: any) => {
      const active = !!(event && (event.active ?? event.isActive));
      storeLintingActive(active);
    });
  } catch {}
}

const LINT_OVERLAY_STYLE_ID = "kuika-lint-overlay";

function __installLintOverlayCssOnce(): void {
  try {
    if (typeof document === "undefined") return;
    if (document.getElementById(LINT_OVERLAY_STYLE_ID)) return;

    const style = document.createElement("style");
    style.id = LINT_OVERLAY_STYLE_ID;

    // bpmn-js-bpmnlint renders the rule id next to every message ("… (kuika/loop-config)").
    // That id is an internal identifier of our own lint plugin; a low-code user has no way to
    // act on it, so the overlay and the issue list show the message only.
    style.textContent = `
.bjsl-issues .rule {
  display: none !important;
}

.tippy-box .bjsl-tooltip li,
.tippy-box .bjsl-tooltip .bjsl-entry,
.tippy-box .bjsl-overlay li,
.djs-overlay-linting .bjsl-tooltip li,
.djs-overlay-linting .bjsl-tooltip .bjsl-entry,
.djs-overlay-linting .bjsl-overlay li {
  display: flex !important;
  align-items: center !important;
  gap: 10px !important;
}

.tippy-box .bjsl-tooltip li > *,
.tippy-box .bjsl-overlay li > *,
.djs-overlay-linting .bjsl-tooltip li > *,
.djs-overlay-linting .bjsl-overlay li > * {
  line-height: 1 !important;
}

.tippy-box .bjsl-tooltip svg,
.tippy-box .bjsl-tooltip img,
.tippy-box .bjsl-tooltip i,
.tippy-box .bjsl-overlay svg,
.tippy-box .bjsl-overlay img,
.tippy-box .bjsl-overlay i,
.djs-overlay-linting .bjsl-tooltip svg,
.djs-overlay-linting .bjsl-tooltip img,
.djs-overlay-linting .bjsl-tooltip i,
.djs-overlay-linting .bjsl-overlay svg,
.djs-overlay-linting .bjsl-overlay img,
.djs-overlay-linting .bjsl-overlay i {
  flex: 0 0 auto !important;
  align-self: center !important;
  vertical-align: middle !important;
}
`;
    document.head.appendChild(style);
  } catch {}
}

let __bpmnWindowFunctionsInstalled = false;

function installBpmnWindowFunctions() {
  if (__bpmnWindowFunctionsInstalled) return;
  __bpmnWindowFunctionsInstalled = true;

  if (!navigator.webdriver) return;

  (window as any).setBPMN = async (xml: string) => {
    const modeler = (window as any).bpmnModeler || (window as any).viewer;
    if (!modeler) {
      console.error("setBPMN: No modeler available");
      return;
    }
    try {
      await modeler.importXML(xml);
      console.log("setBPMN: XML imported successfully");
    } catch (err) {
      console.error("setBPMN: Failed to import XML", err);
    }
  };

  (window as any).getBPMN = async () => {
    const modeler = (window as any).bpmnModeler || (window as any).viewer;
    if (!modeler) {
      console.error("getBPMN: No modeler available");
      return "";
    }
    try {
      const result = await modeler.saveXML({ format: true });
      return result.xml || "";
    } catch (err) {
      console.error("getBPMN: Failed to export XML", err);
      return "";
    }
  };
}
